import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getGigs } from '../../services/gigService';
import { getCurrentUser } from '../../services/authService';
import VenueMap from '../../components/Map/VenueMap';
import Spinner from '../../components/common/Spinner';
import './GigsList.css';

/**
 * GigsList component for displaying available gigs with filtering options
 */
const GigsList = () => {
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [, setUser] = useState(null);
  const [, setTotalGigs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(8); // Reduced for side-by-side layout
  const [mapCenter] = useState(null);
  const [selectedGig, setSelectedGig] = useState(null); // eslint-disable-line no-unused-vars
  const [filters, setFilters] = useState({
    instrument: '',
    location: '',
    dateFrom: '',
    dateTo: '',
    isRecurring: false,
    minPay: ''
  });

  useEffect(() => {
    const init = async () => {
      try {
        // Get current user
        const { user: currentUser } = await getCurrentUser();
        console.log('Current user in GigsList:', currentUser);
        setUser(currentUser);
        
        // Fetch gigs
        fetchGigs();
      } catch (err) {
        console.error('Error initializing gigs page:', err);
      }
    };
    
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debug function to log gigs data
  useEffect(() => {
    console.log('Current gigs data:', gigs);
    
    // Check if we have gigs but they're not displaying
    if (gigs && gigs.length > 0) {
      console.log('Gigs are available but might not be displaying correctly');
      console.log('First gig details:', gigs[0]);
      
      // Verify location data for debugging
      gigs.forEach(gig => {
        if (gig.location_lat && gig.location_lng) {
          console.log(`Gig ${gig.id} has coordinates:`, gig.location_lat, gig.location_lng);
        } else {
          console.warn(`Gig ${gig.id} is missing coordinates!`);
        }
      });
    } else {
      console.log('No gigs available in state');
    }
  }, [gigs]);
  
  const fetchGigs = async (page = currentPage) => {
    try {
      setLoading(true);
      console.log('Fetching gigs for page', page);
      console.log('Using filters:', filters);
      
      // Use the gigService to get gigs with filters
      const { gigs: gigsData, count, totalPages: pages, error: gigsError } = 
        await getGigs(filters, page, pageSize);
      
      console.log('Gigs data received:', gigsData);
      console.log('Total count:', count);
      
      if (gigsError) {
        console.error('Error from gigService:', gigsError);
        throw gigsError;
      }
      
      setGigs(gigsData);
      setTotalGigs(count || 0);
      setTotalPages(pages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching gigs:', error.message);
      console.error('Error details:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter functionality has been removed as requested
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchGigs(newPage);
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="search-container">
      <h1 className="app-title">Unissn</h1>
      
      <div className="search-actions-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input 
              type="text" 
              placeholder="Search" 
              onChange={(e) => {
                // This would be expanded in a real implementation to handle search
                console.log('Search term:', e.target.value);
              }}
            />
          </div>
          <Link to="/gigs/create" className="action-button">Post a Gig</Link>
        </div>
      </div>
      
      {/* Filter Buttons have been removed as requested */}
      
      {/* Gigs Content - Side by Side Layout */}
      {loading ? (
        <div className="my-5">
          <Spinner message="Loading gigs..." fullPage={true} />
        </div>
      ) : (
        <div className="side-by-side-container">
          {/* Left side: Listings */}
          <div className="listings-container">
            <div className="gig-cards">
              {gigs && gigs.length > 0 ? (
            gigs.map(gig => (
              <div className="gig-card" key={gig.id}>
                <div className="gig-info">
                  <div className="gig-details">
                    <div className="venue-info-container">
                      {gig.profiles && (
                        <div className="venue-info">
                          <div className="venue-profile">
                            {gig.profiles.profile_image_url ? (
                              <img 
                                src={gig.profiles.profile_image_url} 
                                alt={gig.profiles.venue_name || gig.profiles.full_name} 
                                className="venue-image"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://via.placeholder.com/50?text=V';
                                }}
                              />
                            ) : (
                              <div className="venue-image-placeholder">
                                {gig.profiles.venue_name ? gig.profiles.venue_name.charAt(0).toUpperCase() : 
                                 gig.profiles.full_name ? gig.profiles.full_name.charAt(0).toUpperCase() : 'V'}
                              </div>
                            )}
                          </div>
                          <div className="venue-details">
                            <p className="venue-name">
                              {gig.profiles.venue_name || gig.profiles.full_name || 'Unknown'}
                            </p>
                            <p className="venue-type">
                              {gig.profiles.user_type === 'venue' ? 'Venue' : 'Organization'}
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="gig-badges">
                        <span className={`gig-badge ${gig.is_recurring ? 'recurring' : 'one-time'}`}>
                          {gig.is_recurring ? 'Recurring' : 'One-time'}
                        </span>
                        <span className="gig-pay">${gig.pay}</span>
                      </div>
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="gig-title-container">
                        {gig.profiles && gig.profiles.profile_image_url && (
                          <div className="gig-artist-image">
                            <img 
                              src={gig.profiles.profile_image_url} 
                              alt={gig.profiles.venue_name || gig.profiles.full_name} 
                              className="artist-image"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/30?text=' + 
                                  (gig.profiles.venue_name ? gig.profiles.venue_name.charAt(0).toUpperCase() : 
                                   gig.profiles.full_name ? gig.profiles.full_name.charAt(0).toUpperCase() : 'V');
                              }}
                            />
                          </div>
                        )}
                        <h2 className="gig-title">{gig.title}</h2>
                      </div>
                      <Link to={`/gigs/${gig.id}`} className="action-button">View Details</Link>
                    </div>
                    
                    <div className="gig-metadata">
                      <div className="metadata-item">
                        <div className="metadata-label">Date</div>
                        <div className="metadata-value">{new Date(gig.date).toLocaleDateString()}</div>
                      </div>
                      
                      <div className="metadata-item">
                        <div className="metadata-label">Time</div>
                        <div className="metadata-value">{gig.time}</div>
                      </div>
                      
                      <div className="metadata-item">
                        <div className="metadata-label">Location</div>
                        <div className="metadata-value">{gig.location}</div>
                      </div>
                      
                      <div className="metadata-item">
                        <div className="metadata-label">Instruments</div>
                        <div className="metadata-value">{gig.instruments}</div>
                      </div>
                    </div>
                    
                    {gig.description && (
                      <div className="mt-3">
                        <p>
                          {gig.description.length > 150
                            ? `${gig.description.substring(0, 150)}...`
                            : gig.description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center my-5">
              <p>No gigs found matching your criteria.</p>
              <button 
                className="filter-button"
                onClick={() => {
                  setFilters({
                    instrument: '',
                    location: '',
                    dateFrom: '',
                    dateTo: '',
                    isRecurring: false,
                    minPay: ''
                  });
                  setTimeout(() => fetchGigs(1), 0);
                }}
              >
                Reset Filters
              </button>
            </div>
              )}
            
              {/* Pagination */}
              {totalPages > 1 && (
            <nav aria-label="Gigs pagination" className="my-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                
                {[...Array(totalPages).keys()].map(page => (
                  <li 
                    key={page + 1} 
                    className={`page-item ${currentPage === page + 1 ? 'active' : ''}`}
                  >
                    <button 
                      className="page-link" 
                      onClick={() => handlePageChange(page + 1)}
                    >
                      {page + 1}
                    </button>
                  </li>
                ))}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
              )}
            </div>
          </div>
          
          {/* Right side: Map */}
          <div className="map-container">
            <VenueMap 
              key={`gig-map-${gigs.length}-${Date.now()}`} // Force remount with unique key
              center={mapCenter} 
              zoom={10} 
              highlightedGigs={gigs.map(gig => ({
                ...gig,
                // Ensure coordinates are explicitly set as numbers
                location_lat: Number(gig.location_lat),
                location_lng: Number(gig.location_lng)
              }))}
              onMarkerClick={(gig) => setSelectedGig(gig)}
            />
            {/* Debug info - hidden from view */}
            <div className="map-debug" style={{display: 'none'}}>
              Gigs available: {gigs.length}
              {gigs.length > 0 && ` (First gig: ${gigs[0]?.title})`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GigsList;
